import { useState } from 'react';
import SEOHead from '../components/common/SEOHead';
import emailjs from '@emailjs/browser';
import {
  EnvelopeIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { FaInstagram, FaYoutube, FaFacebook, FaTiktok } from 'react-icons/fa';
import ParallaxBlob from '../components/common/ParallaxBlob';
import Reveal from '../components/common/Reveal';
import { EMAILJS_CONFIG } from '../config/emailjs';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const now = new Date();
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          name: formData.name,
          initial: formData.name.charAt(0).toUpperCase(),
          time: now.toLocaleString(),
          message: `Subject: ${formData.subject}\n\nEmail: ${formData.email}\n\n${formData.message}`,
          replyLink: `mailto:${formData.email}?subject=Re: ${formData.subject}`,
          year: now.getFullYear().toString(),
        },
        EMAILJS_CONFIG.publicKey
      );
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      alert(
        'Sorry, we could not send your message right now. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    {
      name: 'Instagram',
      icon: FaInstagram,
      href: 'https://www.instagram.com/rumonrahmanmd/',
      color: 'hover:text-pink-500',
    },
    {
      name: 'TikTok',
      icon: FaTiktok,
      href: 'https://www.tiktok.com/@rumonrehman?lang=en',
      color: 'hover:text-gray-900',
    },
    {
      name: 'YouTube',
      icon: FaYoutube,
      href: 'https://www.youtube.com/@mermaidsofthemoment',
      color: 'hover:text-red-500',
    },
    {
      name: 'Facebook',
      icon: FaFacebook,
      href: 'https://www.facebook.com/profile.php?id=100092539025966',
      color: 'hover:text-blue-600',
    },
  ];

  return (
    <>
      <SEOHead
        title="Contact Us"
        description="Get in touch with us! Share your travel stories, ask questions, or collaborate with Mermaids Of The Moment."
        path="/contact"
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden section-padding-sm bg-gradient-to-b from-white via-sky-50 to-indigo-50">
          <ParallaxBlob
            className="-top-24 -left-24"
            from="from-sky-200"
            to="to-indigo-200"
            width="w-80"
            height="h-80"
            speed={0.12}
          />
          <ParallaxBlob
            className="top-6 -right-24"
            from="from-purple-200"
            to="to-pink-200"
            width="w-72"
            height="h-72"
            speed={0.2}
          />
          <div className="container-max text-center relative z-10">
            <div className="max-w-4xl mx-auto">
              <Reveal>
                <img
                  src="/apple-touch-icon.png"
                  alt="logo"
                  className="w-14 h-14 mx-auto mb-4 rounded-2xl shadow ring-1 ring-black/5"
                />
              </Reveal>
              <Reveal delayMs={80}>
                <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 bg-gradient-to-r from-slate-900 via-indigo-800 to-slate-900 bg-clip-text text-transparent">
                  Contact Us
                </h1>
              </Reveal>
              <Reveal delayMs={140}>
                <p className="text-large text-slate-600/90 max-w-3xl mx-auto mb-8">
                  Hey there, fellow traveler! 🌍✈️
                </p>
              </Reveal>
              <Reveal delayMs={220}>
                <div className="bg-white/90 backdrop-blur rounded-3xl shadow-xl p-8 md:p-12 text-left max-w-4xl mx-auto">
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    Got a question, collaboration idea, or just want to say
                    hello? I'd love to hear from you! Whether you're planning
                    your next adventure, need tips for a destination, or want to
                    share your own travel story—drop me a message. 💬
                  </p>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    You can reach out to me anytime through the contact form
                    below or email me directly at{' '}
                    <a
                      href="mailto:rumon1@gmail.com"
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      rumon1@gmail.com
                    </a>
                    .
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Let's connect, inspire, and explore the world together—one
                    blog post at a time. 🗺️✨
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="section-padding bg-gray-50">
          <div className="container-max">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <div className="mb-8">
                  <h2 className="heading-md text-gray-900 mb-4">
                    Send us a Message
                  </h2>
                  <p className="text-gray-600">
                    📩 Wanna talk travel? I'm always up for a good story, a
                    hidden gem recommendation, or just geeking out about maps
                    and airports. Hit me up—don't be shy!
                  </p>
                </div>

                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PaperAirplaneIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-gray-600">
                      Thanks for reaching out! I'll get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Your Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="input-field"
                          placeholder="Enter your name"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="input-field"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="input-field"
                        placeholder="What's this about?"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="textarea-field"
                        placeholder="Tell me about your travel dreams, questions, or just say hi!"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full group"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <PaperAirplaneIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div className="bg-white rounded-3xl shadow-lg p-8">
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">
                    Get in Touch
                  </h3>

                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <EnvelopeIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Email</h4>
                        <a
                          href="mailto:rumon1@gmail.com"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          rumon1@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                        <MapPinIcon className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Location
                        </h4>
                        <p className="text-gray-600">Based in Paradise</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                        <ChatBubbleLeftRightIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Response Time
                        </h4>
                        <p className="text-gray-600">Usually within 24 hours</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="bg-white rounded-3xl shadow-lg p-8">
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">
                    Follow Our Journey
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Stay connected and follow our latest adventures across
                    social media!
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    {socialLinks.map(social => {
                      const IconComponent = social.icon;
                      return (
                        <a
                          key={social.name}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center p-4 border border-gray-200 rounded-2xl hover:border-gray-300 transition-all duration-200 ${social.color} group`}
                        >
                          <IconComponent className="w-6 h-6 mr-3 text-gray-600 group-hover:scale-110 transition-transform" />
                          <span className="font-medium text-gray-700">
                            {social.name}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
